import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, ChevronDown, UserRound } from "lucide-react";
import { WHATSAPP_LINK, STACKCP_URL } from "@/data/site";

const NAV = [
  { label: "Home", to: "/", testid: "nav-link-home" },
  {
    label: "About Spark Plug",
    to: "/#about",
    testid: "nav-link-about",
    children: [
      { label: "About Us", to: "/#about", testid: "nav-link-about-us" },
      { label: "Why Us", to: "/#why-us", testid: "nav-link-why-us" },
      { label: "Blog", to: "/blog", testid: "nav-link-blog" },
    ],
  },
  {
    label: "Services",
    to: "/#services",
    testid: "nav-link-services",
    children: [
      { label: "Hosting", to: "/#hosting", testid: "nav-link-hosting" },
      { label: "All-In-One", to: "/#all-in-one", testid: "nav-link-all-in-one" },
      { label: "Marketing", to: "/#marketing", testid: "nav-link-marketing" },
      { label: "Portfolio", to: "/#portfolio", testid: "nav-link-portfolio" },
    ],
  },
  { label: "Contact Us", to: "/contact", testid: "nav-link-contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center">
    <img
      src="/assets/sparkplug-logo.webp"
      alt="Spark Plug"
      className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
    />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => setOpen(false), [pathname, hash]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#09090B]/70 backdrop-blur-lg">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.testid} className="group relative">
                <Link
                  to={item.to}
                  data-testid={item.testid}
                  className="flex items-center gap-1.5 py-2 text-sm font-medium text-[#A1A1AA] transition-colors duration-300 hover:text-white"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div
                    className="min-w-[190px] rounded-xl border border-white/10 bg-[#131316] p-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                    data-testid={`${item.testid}-dropdown`}
                  >
                    {item.children.map((c) => (
                      <Link
                        key={c.testid}
                        to={c.to}
                        data-testid={c.testid}
                        className="block rounded-lg px-4 py-2.5 text-sm text-[#A1A1AA] transition-colors duration-200 hover:bg-white/5 hover:text-white"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.testid}
                to={item.to}
                data-testid={item.testid}
                className="text-sm font-medium text-[#A1A1AA] transition-colors duration-300 hover:text-white"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors duration-300 hover:border-[#D9A92E]/60 hover:text-[#E9C766]"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
          <a
            href={STACKCP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-my-account-button"
            className="flex items-center gap-2 rounded-full bg-[#D9A92E] px-5 py-2 text-sm font-semibold text-[#09090B] shadow-[0_0_25px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] hover:shadow-[0_0_35px_rgba(217,169,46,0.6)]"
          >
            <UserRound className="h-4 w-4" />
            My Account
          </a>
        </div>
        <button
          className="text-white lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#131316]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV.map((item) => (
                <div key={item.testid}>
                  <Link
                    to={item.to}
                    data-testid={`mobile-${item.testid}`}
                    className="block rounded-lg px-3 py-3 text-base font-medium text-[#A1A1AA] transition-colors hover:bg-white/5 hover:text-white"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="ml-4 border-l border-white/10 pl-3">
                      {item.children.map((c) => (
                        <Link
                          key={c.testid}
                          to={c.to}
                          data-testid={`mobile-${c.testid}`}
                          className="block rounded-lg px-3 py-2 text-sm text-[#6B7280] transition-colors hover:text-white"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={STACKCP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-my-account-button"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#D9A92E] px-5 py-3 text-sm font-semibold text-[#09090B]"
              >
                <UserRound className="h-4 w-4" />
                My Account
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
